import { LazoPaths } from "@/enums/LazoPaths";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { AddOnObjectKeyPathMap, AddOnObjectKeyType } from "@/models/subscriptions/AddOn";
import { SubscriptionPurchase } from "@/models/subscriptions/SubscriptionPurchase";
import LazoAuthService from "@/services/LazoAuthService";

export const PostLoginRedirectStorageKey = "postLoginRedirect";
export type AccessMapType = { [k: string]: boolean };

export function generateAccessMap(subscriptionPurchase: SubscriptionPurchase): AccessMapType {
  let addons = [];
  for (const addon of subscriptionPurchase.subscription.addOns) {
    addons.push(addon.objectKey);
  }
  for (const addon of subscriptionPurchase.extraAddOns) {
    addons.push(addon.objectKey);
  }

  const accessMap: AccessMapType = Object.keys(AddOnObjectKeyPathMap).reduce((acc, k) => {
    let found = false;

    if (AddOnObjectKeyPathMap[k].some((a: AddOnObjectKeyType) => addons.includes(a))) {
      found = true;
    }

    acc[k] = found;
    return acc;
  }, {});

  return accessMap;
}

function getRedirectUrl(slug: string) {
  return getHrefBySlug(slug) + LazoPaths.REDIRECT;
}

export function redirectToWorkspace(slug: string, tenantExternalId?: string, currentPathname?: string) {
  const redirectUrl = getRedirectUrl(slug);

  const params = new URLSearchParams();
  if (currentPathname) {
    params.set("p", currentPathname);
  }
  if (tenantExternalId) {
    params.set("tm", tenantExternalId);
  }

  const token = AuthService.getToken();
  const expiresAt = AuthService.getTokenExpiration();

  LazoAuthService.workspaceRedirect(token, expiresAt).then((r) => {
    r.onSuccess((c) => {
      params.set("otc", c.otc);
      window.location.replace(`${redirectUrl}?${params}`);
    }).onError(() => NotificationService.error("There was an error redirecting to workspace"));
  });
}

// Pure host/env -> workspace URL logic, extracted so it can be unit-tested without jsdom's
// non-configurable window.location. getHrefBySlug is the thin wrapper that supplies the live host.
export function buildHrefBySlug(slug: string, host: string, env: string) {
  const hostArr = host.split(".");

  if (env === "dev") {
    let localDomain = "";

    if (hostArr.length === 2) {
      localDomain = hostArr[1];
    } else if (hostArr.length === 1) {
      localDomain = hostArr[0];
    } else {
      throw new Error("Error redirecting to workspace, invalid number of domains");
    }

    return `http://${slug}-dev.${localDomain}`;
  }

  // QA workspaces live under the dedicated qa.lazo.one subzone (<slug>.qa.lazo.one), so prepend
  // the slug to the current QA host (qa.lazo.one). Mirrors Route53Service's QA branch.
  if (env.toLowerCase() === "qa") {
    return `https://${slug}.${host}`;
  }

  // Handle prod subdomain redirection (<slug>.lazo.one): strip the leading app. label.
  let remoteDomain = "";
  const suffix = hostArr[hostArr.length - 1];
  if (hostArr.length === 3 || hostArr.length === 2) {
    remoteDomain = hostArr[1];
  } else {
    throw new Error("Error redirecting to workspace, invalid number of domains");
  }

  return `https://${slug}.${remoteDomain}.${suffix}`;
}

export function getHrefBySlug(slug: string) {
  return buildHrefBySlug(slug, window.location.host, environment.env);
}

export const LAZO_SLUGS_BY_ENV = {
  DEV: "lazo",
  QA: "lazo",
  PROD: "lazo_space",
};

// First hostname labels that identify the ROOT app hosts (app.lazo.one, qa.lazo.one,
// dev.lazo.one[:8443], localhost, www) as opposed to a workspace subdomain
// (<slug>.lazo.one / <slug>.qa.lazo.one / <slug>-dev.lazo.one). Mirrors the label logic of the
// backend's extractSlugFromOrigin.
const ROOT_APP_FIRST_LABELS = ["app", "qa", "dev", "www", "localhost"];

/**
 * True when the given hostname is a workspace subdomain (a slug-scoped origin where a session
 * can be recovered from the Domain=.lazo.one cookie). False on the root app hosts, where login
 * must go through the OTC handoff to the user's workspace instead.
 */
export function isWorkspaceHost(hostname: string): boolean {
  const firstLabel = hostname.split(".")[0];
  return !ROOT_APP_FIRST_LABELS.includes(firstLabel);
}
